// Phase P-LINK-APOSTILLE-TRIPLE
// Forward edges from /[en/]apostille/$country into the triple-key translation
// matrix /[en/]translate/english/$doctype/in/$location.
//
// Why: Apostille / Legalization is almost always preceded by certified English
// translation. Deep-linking to the most-converting triple-key combos closes the
// user journey and funnels PageRank from ~51 apostille country pages (TH+EN)
// to the long-tail triple-key money pages.

import { Link } from "@tanstack/react-router";
import { ArrowRight, Languages } from "lucide-react";
import { getTranslationDocType } from "@/data/translation-doctypes";

// Doctypes most often apostilled. Order = display priority; caps at 6.
const DEFAULT_APOSTILLE_DOCTYPES = [
  "birth-certificate",
  "marriage-certificate",
  "diploma",
  "transcript",
  "police-clearance",
  "divorce-certificate",
  "house-registration",
  "id-card",
] as const;

// Hub whitelist — subset of TRIPLE_KEY_LOCATIONS
const DEFAULT_HUBS = [
  "bangkok-hq",
  "hub-sathorn-cbd",
  "hub-asoke-corridor",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-pattaya",
] as const;

const HUB_LABEL_TH: Record<string, string> = {
  "bangkok-hq": "สำนักงานใหญ่ กรุงเทพฯ",
  "hub-asoke-corridor": "อโศก / สุขุมวิท",
  "hub-sathorn-cbd": "สาทร CBD",
  "hub-phuket-patong": "ภูเก็ต ป่าตอง",
  "hub-chiangmai-nimman": "เชียงใหม่ นิมมาน",
  "hub-pattaya": "พัทยา",
};
const HUB_LABEL_EN: Record<string, string> = {
  "bangkok-hq": "Bangkok HQ",
  "hub-asoke-corridor": "Asoke / Sukhumvit",
  "hub-sathorn-cbd": "Sathorn CBD",
  "hub-phuket-patong": "Phuket / Patong",
  "hub-chiangmai-nimman": "Chiang Mai / Nimman",
  "hub-pattaya": "Pattaya",
};

interface Props {
  mode: "th" | "en";
  countryNameTh: string;
  countryNameEn: string;
  isHague: boolean;
}

export default function ApostilleTripleKeyLinks({
  mode,
  countryNameTh,
  countryNameEn,
  isHague,
}: Props) {
  const isTh = mode === "th";
  const label = isHague ? (isTh ? "Apostille" : "Apostille") : (isTh ? "Legalization" : "Legalization");

  type Card = { slug: string; d: NonNullable<ReturnType<typeof getTranslationDocType>>; hub: string };
  const cards: Card[] = DEFAULT_APOSTILLE_DOCTYPES
    .map((slug, i): Card | null => {
      const d = getTranslationDocType(slug);
      if (!d) return null;
      const hub = DEFAULT_HUBS[i % DEFAULT_HUBS.length];
      return { slug: slug as string, d, hub: hub as string };
    })
    .filter((x): x is Card => x !== null)
    .slice(0, 6);

  if (cards.length === 0) return null;

  return (
    <section className="mt-14 border-t border-slate-200 pt-10">
      <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
        {isTh ? `แปลก่อน ${label} ส่ง${countryNameTh}` : `Translate before ${label} → ${countryNameEn}`}
      </div>
      <h2 className="mt-1 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
        {isTh
          ? `เอกสารยอดนิยมที่ต้องแปลภาษาอังกฤษก่อน ${label} ส่ง${countryNameTh}`
          : `Documents most often translated into English before ${label} for ${countryNameEn}`}
      </h2>
      <p className="mt-2 max-w-3xl text-sm text-slate-600">
        {isTh
          ? `เลือกเอกสารและฮับที่ใกล้คุณ — รับรอง MoJ + ตรา MFA ในที่เดียว ก่อนเข้าขั้น ${label} เพื่อใช้ที่${countryNameTh}`
          : `Pick the document and the hub nearest you — MoJ-sworn + MFA-stamped here, ready for the ${label} step toward ${countryNameEn}.`}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map(({ slug, d, hub }) => (
          <Link
            key={`${slug}-${hub}`}
            to={isTh
              ? "/translate/$language/$doctype/in/$location"
              : "/en/translate/$language/$doctype/in/$location"}
            params={{ language: "english", doctype: slug, location: hub }}
            className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
            data-track-zone="apostille-triple-deep"
          >
            <Languages className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                {isTh ? d.nameTh : d.nameEn}
              </div>
              <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                {isTh
                  ? `แปล ${d.nameTh} เป็นภาษาอังกฤษ`
                  : `${d.nameEn} → English`}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {isTh ? `รับ/นัดที่ ${HUB_LABEL_TH[hub] ?? hub}` : `Pick-up at ${HUB_LABEL_EN[hub] ?? hub}`}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 group-hover:gap-1.5">
                {isTh ? "ดูราคา + ขั้นตอน" : "See price + steps"} <ArrowRight className="h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
